"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { usePathname } from "next/navigation";
import {
  analytics,
  sendGAEvent,
  trackEvent,
} from "@/lib/analytics";

export function useAnalytics() {
  const pathname = usePathname();

  // Button click tracking
  const trackButtonClick = useCallback((buttonName, metadata = {}) => {
    analytics.trackButtonClick(buttonName, pathname, metadata);
  }, [pathname]);

  // Form submission tracking
  const trackFormSubmit = useCallback((formName, metadata = {}) => {
    analytics.trackFormSubmit(formName, pathname, metadata);
  }, [pathname]);

  // Link click tracking
  const trackLinkClick = useCallback((linkText, linkUrl, isExternal = false) => {
    analytics.trackLinkClick(linkText, linkUrl, isExternal);
  }, []);

  // Search tracking
  const trackSearch = useCallback((searchTerm) => {
    analytics.trackSearch(searchTerm, pathname);
  }, [pathname]);

  // Simulation tracking
  const trackSimulationStart = useCallback((simulationType, persona) => {
    analytics.trackSimulationStart(simulationType, persona);
  }, []);

  const trackSimulationComplete = useCallback((simulationType, score, duration) => {
    analytics.trackSimulationComplete(simulationType, score, duration);
  }, []);

  // Feedback tracking
  const trackFeedbackGenerated = useCallback((feedbackType, simulationId) => {
    analytics.trackFeedbackGenerated(feedbackType, simulationId);
  }, []);

  // Video tracking
  const trackVideoPlay = useCallback((videoTitle, videoUrl) => {
    analytics.trackVideoPlay(videoTitle, videoUrl);
  }, []);

  const trackVideoProgress = useCallback((videoTitle, percentage) => {
    analytics.trackVideoProgress(videoTitle, percentage);
  }, []);

  // File download tracking
  const trackFileDownload = useCallback((fileName, fileType) => {
    analytics.trackFileDownload(fileName, fileType);
  }, []);

  // Auth tracking
  const trackSignUp = useCallback((method) => {
    analytics.trackSignUp(method, pathname);
  }, [pathname]);

  const trackLogin = useCallback((method) => {
    analytics.trackLogin(method);
  }, []);

  // Error tracking
  const trackError = useCallback((errorMessage) => {
    analytics.trackError(errorMessage, pathname);
  }, [pathname]);

  // Generic event tracking
  const track = useCallback((eventName, parameters = {}) => {
    sendGAEvent(eventName, parameters);
  }, []);

  // Legacy event tracking (for backward compatibility)
  const event = useCallback((action, category, label, value, customParameters = {}) => {
    trackEvent(action, category, label, value, customParameters);
  }, []);

  return {
    // Core tracking functions
    track,
    event,
    
    // Specific tracking functions
    trackButtonClick,
    trackFormSubmit,
    trackLinkClick,
    trackSearch,
    trackSimulationStart,
    trackSimulationComplete,
    trackFeedbackGenerated,
    trackVideoPlay,
    trackVideoProgress,
    trackFileDownload,
    trackSignUp,
    trackLogin,
    trackError,

    // Direct access to analytics object for advanced use
    analytics,
  };
}

// Hook for tracking scroll depth
export function useScrollTracking(initialThresholds = [25, 50, 75, 90, 100]) {
  // Memoize thresholds to prevent re-renders
  const thresholds = useMemo(() => initialThresholds, []);
  const trackedThresholdsRef = useRef(new Set());

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY;
      
      // Prevent division by zero - if scrollHeight is 0 or negative, we're at 100%
      const scrollPercentage = scrollHeight > 0 
        ? Math.round((scrolled / scrollHeight) * 100)
        : 100;

      thresholds.forEach(threshold => {
        if (scrollPercentage >= threshold && !trackedThresholdsRef.current.has(threshold)) {
          trackedThresholdsRef.current.add(threshold);
          analytics.trackScroll(threshold);
        }
      });
    };

    // Debounce scroll events
    let scrollTimer;
    const debouncedScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(handleScroll, 150);
    };

    window.addEventListener("scroll", debouncedScroll);

    return () => {
      window.removeEventListener("scroll", debouncedScroll);
      clearTimeout(scrollTimer);
    };
  }, [thresholds]);
}

// Hook for tracking time on page
export function useTimeOnPage(pageName) {
  useEffect(() => {
    const startTime = Date.now();

    const trackTimeSpent = () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000); // in seconds
      sendGAEvent("time_on_page", {
        page_name: pageName,
        time_seconds: timeSpent,
      });
    };

    // Track when user leaves the page - use pagehide for better reliability
    // especially on mobile where beforeunload may not fire
    window.addEventListener("pagehide", trackTimeSpent);
    
    // Also listen to beforeunload as fallback for desktop browsers
    window.addEventListener("beforeunload", trackTimeSpent);

    // Also track when component unmounts
    return () => {
      window.removeEventListener("pagehide", trackTimeSpent);
      window.removeEventListener("beforeunload", trackTimeSpent);
      trackTimeSpent();
    };
  }, [pageName]);
}

// Hook for tracking element visibility
export function useVisibilityTracking(elementRef, eventName, threshold = 0.5) {
  useEffect(() => {
    if (!elementRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            sendGAEvent(eventName, {
              element_visible: true,
              visibility_ratio: entry.intersectionRatio,
            });
            observer.disconnect(); // Track only once
          }
        });
      },
      { threshold }
    );

    observer.observe(elementRef.current);

    return () => {
      observer.disconnect();
    };
  }, [elementRef, eventName, threshold]);
}
